import { useEffect, useState } from "react";
import { Users, CalendarCheck, ClipboardList, BedDouble, IndianRupee, Stethoscope } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { KpiCard, PageHeader, Loading, ErrorState } from "@/components/Bits";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inrShort, num } from "@/lib/format";

export default function HospitalDashboard() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!can("hospital.dashboard")) return;
    api.get("/hospital/dashboard").then(({ data }) => setD(data)).catch((e) => setError(apiError(e)));
  }, [can]);

  if (!can("hospital.dashboard")) return <NoAccess what="the hospital dashboard" />;
  if (error) return <ErrorState message={error} />;
  if (!d) return <Loading label="Loading hospital dashboard…" />;

  return (
    <div data-testid="hospital-dashboard">
      <PageHeader overline="Hospital" title="Hospital dashboard"
        subtitle="Live operational and financial position. Every card opens the underlying records." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Registered patients" value={num(d.patients)} icon={Users} to="/app/hospital/patients" testid="kpi-patients" />
        <KpiCard label="Appointments today" value={num(d.appointments_today)} icon={CalendarCheck} to="/app/hospital/appointments" testid="kpi-appointments-today" />
        <KpiCard label="OPD today" value={num(d.opd_today)} hint={`${num(d.opd_waiting)} waiting in queue`} icon={ClipboardList} to="/app/hospital/opd" testid="kpi-opd-today" />
        <KpiCard label="IPD admitted" value={num(d.ipd_active)} icon={BedDouble} to="/app/hospital/ipd" testid="kpi-ipd-active" />
        <KpiCard label="Beds available" value={`${num(d.beds_available)} / ${num(d.beds_total)}`} hint={`${num(d.beds_occupied)} occupied`} icon={BedDouble} to="/app/hospital/ipd" testid="kpi-beds" />
        <KpiCard label="Doctors" value={num(d.doctors)} hint={`${num(d.departments)} departments`} icon={Stethoscope} to="/app/hospital/doctors" testid="kpi-doctors" />
        <KpiCard label="Revenue today" value={inrShort(d.revenue_today)} tone="accent" icon={IndianRupee} to="/app/hospital/billing" testid="kpi-revenue-today" />
        <KpiCard label="Outstanding" value={inrShort(d.revenue_outstanding)} tone="danger" icon={IndianRupee} to="/app/hospital/billing" testid="kpi-outstanding" />
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <KpiCard label="Total billed" value={inrShort(d.revenue_total)} to="/app/hospital/reports" testid="kpi-total-billed" />
        <KpiCard label="Collected" value={inrShort(d.revenue_collected)} tone="success" to="/app/hospital/reports" testid="kpi-collected" />
        <KpiCard label="Pending appointments" value={num(d.appointments_pending)} to="/app/hospital/appointments" testid="kpi-pending-appointments" />
      </div>
    </div>
  );
}
